import AirDatepicker from 'air-datepicker';
import localeNl from 'air-datepicker/locale/nl';
import { toggleAriaExpanded } from './utils';
import noUiSlider from 'nouislider';

const container = document.querySelector('.archive-container');

let archiveFilter;
let archiveFilterToggle;
let dateFilter;
let distanceFilter;
let datePicker;
let distanceSlider;
let resetAllButton;
let resetButtons;
let filterOpen = false;
let filtering = false;
let documentEventsBound = false;

function closeDropdown(dropdown) {
    const button = dropdown.querySelector('.archive-filter-dropdown-toggle');

    dropdown.classList.remove('open');

    if (button) {
        button.setAttribute('aria-expanded', 'false');
    }
}

function closeAllDropdowns(except = null) {
    archiveFilter?.querySelectorAll('.archive-filter-dropdown').forEach(dropdown => {
        if (dropdown !== except) {
            closeDropdown(dropdown);
        }
    });
}

function updateScrollableState(scrollContainer) {
    const hasOverflow = scrollContainer.scrollHeight > scrollContainer.clientHeight + 1;
    const isAtBottom = scrollContainer.scrollTop + scrollContainer.clientHeight >= scrollContainer.scrollHeight - 1;

    scrollContainer.classList.toggle('is-scrollable', hasOverflow);
    scrollContainer.classList.toggle('is-scrolled-end', hasOverflow && isAtBottom);
}

function initDropdowns() {
    const dropdowns = archiveFilter.querySelectorAll('.archive-filter-dropdown');

    dropdowns.forEach(dropdown => {
        const toggle = dropdown.querySelector('.archive-filter-dropdown-toggle');

        if (!toggle) {
            return;
        }

        toggle.addEventListener('click', () => {
            const isOpen = dropdown.classList.contains('open');

            closeAllDropdowns(isOpen ? null : dropdown);
            dropdown.classList.toggle('open', !isOpen);
            toggle.setAttribute('aria-expanded', String(!isOpen));

            if (!isOpen) {
                requestAnimationFrame(() => {
                    dropdown.querySelectorAll('.archive-filter-scroll').forEach(scrollContainer => {
                        updateScrollableState(scrollContainer);
                    });
                });
            }
        });
    });

    archiveFilter.querySelectorAll('.archive-filter-scroll').forEach(scrollContainer => {
        const hasChoiceInputs = scrollContainer.querySelector('input[type="checkbox"], input[type="radio"]');

        if (!hasChoiceInputs) {
            return;
        }

        updateScrollableState(scrollContainer);

        scrollContainer.addEventListener('scroll', () => {
            updateScrollableState(scrollContainer);
        });
    });

    if (!documentEventsBound) {
        document.addEventListener('click', event => {
            const isDatepickerInteraction = event.target.closest('.air-datepicker');

            if (isDatepickerInteraction) {
                return;
            }

            if (!archiveFilter?.contains(event.target)) {
                closeAllDropdowns();
            }
        });

        document.addEventListener('keydown', event => {
            if (event.key === 'Escape') {
                closeAllDropdowns();
            }
        });

        window.addEventListener('resize', () => {
            archiveFilter?.querySelectorAll('.archive-filter-scroll').forEach(scrollContainer => {
                updateScrollableState(scrollContainer);
            });
        });

        documentEventsBound = true;
    }
}

function initFilter() {
    archiveFilter = document.querySelector('.archive-filters');

    if (!archiveFilter) {
        return;
    }

    archiveFilterToggle = document.querySelector('.archive-filters-toggle');
    dateFilter = document.querySelector('.archive-filters-date');
    distanceFilter = document.querySelector('.archive-filters-distance');
    resetAllButton = document.querySelector('.archive-resets-all');
    resetButtons = document.querySelectorAll('.archive-resets-button');

    initDropdowns();

    archiveFilter.addEventListener('submit', e => {
        e.preventDefault();

        closeAllDropdowns();
        executeFilter();
    });

    archiveFilter.addEventListener('change', e => {
        // When a subchoice is checked, uncheck the parent checkbox
        if (e.target.type === 'checkbox' && e.target.checked && e.target.closest('.archive-filters-subchoices')) {
            const parentChoice = e.target.closest('.archive-filters-subchoices').previousElementSibling;
            const parentCheckbox = parentChoice.querySelector('input[type="checkbox"]');
            parentCheckbox.checked = false;
        }
    });
    
    archiveFilterToggle?.addEventListener('click', () => {
        filterOpen = !filterOpen;
        toggleAriaExpanded(archiveFilterToggle);
        archiveFilter.classList.toggle('open', filterOpen);
    });

    resetAllButton?.addEventListener('click', () => {
        resetFilter();
        executeFilter();
    });

    resetButtons?.forEach(button => {
        button.addEventListener('click', () => {
            resetFilter(button.dataset.name, button.dataset.value);
            executeFilter();
        });
    });

    if (dateFilter) {
        const dateInputText = dateFilter.querySelector('.archive-filters-date-input-text');
        const dateInputHidden = dateFilter.querySelector('.archive-filters-date-input-hidden');
        const datePresets = document.querySelector('.archive-filters-date-presets');

        function formatDate(date) {
            const year = date.getFullYear();
            const month = String(date.getMonth() + 1).padStart(2, '0'); // Months are 0-indexed
            const day = String(date.getDate()).padStart(2, '0');

            return `${year}-${month}-${day}`;
        };

        datePicker = new AirDatepicker(dateInputText, {
            locale: localeNl,
            range: true,
            isMobile: window.matchMedia('(max-width: 768px)').matches,
            autoClose: true,
            multipleDatesSeparator: ' - ',
            dateFormat: 'dd-MM-yyyy',
            onSelect({ date }) {
                if (date.length === 2) {
                    dateInputHidden.value = date.map(formatDate).join(',');
                } else if (date.length === 1) {
                    dateInputHidden.value = formatDate(date[0]);
                } else {
                    dateInputHidden.value = '';
                }
            }
        });

        if (dateInputHidden.value) {
            const dates = dateInputHidden.value.split(',');
            
            if (dates.length === 2) {
                datePicker.selectDate([new Date(dates[0]), new Date(dates[1])]);
            } else if (dates.length === 1) {
                datePicker.selectDate(new Date(dates[0]));
            }
        }

        datePresets.addEventListener('change', e => {
            if (e.target.value === 'custom') {
                dateFilter.classList.add('visible');
                datePicker.show();
            } else {
                resetAndHideDatePicker();
            }
        });
    }

    if (distanceFilter) {
        const container = distanceFilter.querySelector('.archive-filters-distance-slider');
        const minInput = distanceFilter.querySelector('.archive-filters-distance-min');
        const maxInput = distanceFilter.querySelector('.archive-filters-distance-max');
        const minDefault = 0;
        const maxDefault = parseInt(maxInput.getAttribute('max'));
        const minValue = parseInt(minInput.value);
        const maxValue = parseInt(maxInput.value);

        distanceSlider = noUiSlider.create(container, {
            start: [minValue, maxValue],
            connect: true,
            range: {
                min: minDefault,
                max: maxDefault,
            },
            format: {
                to: value => parseInt(value),
                from: value => parseInt(value),
            },
            step: 1,
        });

        distanceSlider.on('update', function (values, handle) {
            if (handle === 0) {
                minInput.value = values[handle];
            } else if (handle === 1) {
                maxInput.value = values[handle];
            }
        });

        distanceSlider.reset = function () {
            distanceSlider.set([minDefault, maxDefault]);
        };
        
        minInput.addEventListener('input', function () {
            distanceSlider.set([this.value, null]);
        });

        maxInput.addEventListener('input', function () {
            distanceSlider.set([null, this.value]);
        });
    }
}

function executeFilter() {
    if (filtering || !archiveFilter || !container) return;

    filtering = true;

    const queryString = new URLSearchParams(new FormData(archiveFilter)).toString();
    const url = archiveFilter.action + '?' + queryString;

    container.classList.add('loading');
    history.pushState({}, '', url);

    fetch(url)
        .then(response => response.text())
        .then(html => {
            const temp = document.createElement('div');
            temp.innerHTML = html;

            const newContainer = temp.querySelector('.archive-container');

            if (newContainer) {
                container.innerHTML = newContainer.innerHTML;
            }
            
            initFilter();

            container.classList.remove('loading');
            archiveFilter.classList.remove('open');
        })
        .finally(() => {
            filtering = false;
        });
}

function resetFilter(name = '', value = '') {
    const selector = name ? `[name="${name}"]` : 'input, select';

    archiveFilter.querySelectorAll(selector).forEach(input => {
        if ((input.type === 'checkbox' || input.type === 'radio') && (!value || input.value === value)) {
            input.checked = false;
        }

        if (['text', 'date', 'search'].includes(input.type)) {
            input.value = '';
        }
    });

    if (!name || name === '_period') {
        resetAndHideDatePicker();
    }

    if (!name || name === '_distance') {
        resetDistanceSlider();
    }
}

function resetAndHideDatePicker() {
    if (!datePicker) {
        return;
    }

    datePicker.clear();

    if (datePicker.visible) {
        datePicker.hide();
    }

    dateFilter.classList.remove('visible');
}

function resetDistanceSlider() {
    if (!distanceSlider) {
        return;
    }

    distanceSlider.reset();
}

initFilter();